import { FC, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Spinner from 'src/components/ui/Features/Spinner/Spinner';
import useFetch from 'src/hook/api/useFetch';
import useUser from 'src/hook/data/useUser';
import { useToast } from 'src/hook/global/useToasts';
import { AgeGroup, HttpMethod, ResponseType } from 'src/model/enum/enums';
import { resizeImage } from 'src/utils/ImageUtils';
import { GetUserPhotoUrl, ImageLocation } from 'src/utils/URLUtils';
import AvatarModal from '../AvatarModal/AvatarModal';
import styles from './Avatar.module.scss';

const Avatar: FC = () => {
    const { t } = useTranslation();
    const { userInfo } = useUser();
    const { send } = useFetch();
    const [showAvatarModal, setShowAvatarModal] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [avatarVersion, setAvatarVersion] = useState<number>(Date.now());
    const fileInputRef = useRef<HTMLInputElement>(null);
    const toast = useToast();
    const defaultAvatar = useMemo(() =>
        ImageLocation(`Profile-${Math.floor(Math.random() * 6) + 1}.png`),
        []);

    const handleAvatarClick = () => {
        if (userInfo?.AgeGroup === AgeGroup.Child) {
            setShowAvatarModal(true);
        } else {
            fileInputRef.current?.click();
        }
    };

    const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Check if file is an image
        if (!file.type.startsWith('image/')) {
            toast.showError(t('Invalid photo'));
            return;
        }

        setIsUploading(true);

        try {
            // Resize the image
            const resizedBlob = await resizeImage(file);

            // Convert blob to base64
            const reader = new FileReader();
            const base64 = await new Promise<string>((resolve) => {
                reader.onload = () => resolve(reader.result as string);
                reader.readAsDataURL(resizedBlob);
            });

            // Remove data URL prefix
            const base64Data = base64.split(',')[1];

            // Prepare the request
            const { response } = await send(
                GetUserPhotoUrl(),
                HttpMethod.POST,
                {
                    Data: base64Data,
                    UserId: userInfo?.UserId,
                    Ticket: userInfo?.LastTicket
                },
                ResponseType.Text
            );

            if (response) {
                // Update the version to force cache refresh
                setAvatarVersion(Date.now());
            } else {
                toast.showError(t('Failed to upload the photo.\nPlease try again later.'));
            }

        } catch (error) {
            console.error('Error uploading image:', error);
            toast.showError(t('Failed to upload the photo.\nPlease try again later.'));
        } finally {
            setIsUploading(false);
        }
    };

    const getAvatarSrc = () => {
        if (userInfo?.Avatar) {
            return `${userInfo.Avatar}?version=${avatarVersion}`;
        }
        return defaultAvatar;
    };

    if (isUploading) {
        return <Spinner />;
    }

    return (
        <>
            <img
                className={styles.avatar}
                onClick={handleAvatarClick}
                src={getAvatarSrc()}
                alt={t('Avatar')}
                onError={(e) => {
                    e.currentTarget.src = defaultAvatar;
                }}
            />
            <input
                type="file"
                ref={fileInputRef}
                accept="image/*"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
            />
            <AvatarModal isModalOpen={showAvatarModal} setIsModalOpen={setShowAvatarModal} />
        </>
    );
};

export default Avatar; 